/** Shared immutable paths, separate from numeric electrical graph handles.
 * Expanded text exists only at file/checkpoint/debug compatibility boundaries. */
export interface RuntimeScope {
  readonly kind: 0 | 1; // placed instance / lifted lane
  readonly value: string | number;
  readonly inner?: RuntimeScope;
}
export interface ScopedRuntimeIdentity {
  readonly leaf: string;
  readonly scope: RuntimeScope;
}
export type RuntimeIdentity = string | ScopedRuntimeIdentity;
export function runtimeIdentity(leaf: string): string {
  return leaf;
}
export function runtimeIdentityText(identity: RuntimeIdentity): string {
  if (typeof identity === 'string') return identity;
  let text = identity.leaf;
  const scopes: RuntimeScope[] = [];
  for (let scope: RuntimeScope | undefined = identity.scope; scope; scope = scope.inner)
    scopes.push(scope);
  for (let i = scopes.length - 1; i >= 0; i--) {
    const scope = scopes[i];
    text =
      scope.kind === 0
        ? `chip:${(scope.value as string).length}:${scope.value}${text}`
        : JSON.stringify(['lift', scope.value, text]);
  }
  return text;
}

/** One cache per graph wrapping operation. Every node sharing an inner scope
 * also shares its new outer scope; nothing survives through a global interner. */
export class RuntimeScopeBuilder {
  readonly #scopes = new Map<RuntimeScope | undefined, RuntimeScope>();
  constructor(
    private readonly kind: 0 | 1,
    private readonly value: string | number,
  ) {}
  wrap(identity: RuntimeIdentity): ScopedRuntimeIdentity {
    const inner = typeof identity === 'string' ? undefined : identity.scope;
    let scope = this.#scopes.get(inner);
    if (!scope) {
      scope = Object.freeze({
        kind: this.kind,
        value: this.value,
        inner,
      });
      this.#scopes.set(inner, scope);
    }
    return Object.freeze({
      leaf: typeof identity === 'string' ? identity : identity.leaf,
      scope,
    });
  }
}

/** Dense tokens live only for one old/new state-restore operation. Native Maps
 * provide exact atom equality; no hashes, collision assumptions or long paths.
 *
 * Saved/opaque leaves can themselves spell a composed path. In that case keep
 * the existing exact-text comparison for the whole operation. No parsing or
 * normalization of imported identities, and no retained substring backing data. */
export class RuntimeIdentityTokens {
  readonly #text: boolean;
  readonly #textTokens = new Map<string, number>();
  readonly #scopeTokens = new WeakMap<RuntimeScope, number>();
  readonly #scopeShapes = new Map<0 | 1, Map<string | number, Map<number, number>>>();
  readonly #identities = new Map<number, Map<string, number>>();
  #nextScope = 1;
  #nextIdentity = 1;
  constructor(identities: Iterable<RuntimeIdentity | undefined>) {
    this.#text = false;
    for (const identity of identities) {
      const leaf = typeof identity === 'string' ? identity : identity?.leaf;
      if (
        leaf !== undefined &&
        (leaf.startsWith('chip:') || leaf.startsWith('["lift",'))
      ) {
        this.#text = true;
        break;
      }
    }
  }
  token(identity: RuntimeIdentity): number {
    if (this.#text) {
      const text = runtimeIdentityText(identity);
      let token = this.#textTokens.get(text);
      if (token === undefined) {
        token = this.#nextIdentity++;
        this.#textTokens.set(text, token);
      }
      return token;
    }
    let scopeToken = 0;
    const pending: RuntimeScope[] = [];
    for (
      let scope = typeof identity === 'string' ? undefined : identity.scope;
      scope;
      scope = scope.inner
    ) {
      const prior = this.#scopeTokens.get(scope);
      if (prior !== undefined) {
        scopeToken = prior;
        break;
      }
      pending.push(scope);
    }
    for (let i = pending.length - 1; i >= 0; i--) {
      const scope = pending[i];
      let byAtom = this.#scopeShapes.get(scope.kind);
      if (!byAtom) this.#scopeShapes.set(scope.kind, (byAtom = new Map()));
      let byInner = byAtom.get(scope.value);
      if (!byInner) byAtom.set(scope.value, (byInner = new Map()));
      let token = byInner.get(scopeToken);
      if (token === undefined) {
        token = this.#nextScope++;
        byInner.set(scopeToken, token);
      }
      this.#scopeTokens.set(scope, token);
      scopeToken = token;
    }
    let leaves = this.#identities.get(scopeToken);
    if (!leaves) this.#identities.set(scopeToken, (leaves = new Map()));
    const leaf = typeof identity === 'string' ? identity : identity.leaf;
    let token = leaves.get(leaf);
    if (token === undefined) {
      token = this.#nextIdentity++;
      leaves.set(leaf, token);
    }
    return token;
  }
}
